import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useMount } from '../hooks/useMount';
import { DICT } from '../lib/i18n';
import { THEMES, applyTheme, pickRandomTheme } from '../lib/themes';
import { OLT_BRANDS, pickRandomOlt } from '../components/OltVisual';
import { geoAPI, statsAPI } from '../lib/api';

// Backoff de retry do refreshPermissions antes de assumir falha definitiva (nega tudo).
const PERMISSIONS_RETRY_DELAYS_MS = [2000, 5000, 10000];

const AppCtx = createContext(null);
const ThroughputCtx = createContext(null);

export function AppProvider({ children }) {
  const [lang, setLang] = useState('en');
  const [theme, setTheme] = useState(THEMES[0]);
  const [mode, setMode] = useState('light');
  const [sectionOrder, setSectionOrder] = useState(['features', 'vendors', 'stats', 'pricing', 'lead']);
  const [stats, setStats] = useState(null);
  const [geoResolved, setGeoResolved] = useState(false);
  const [countryCode, setCountryCode] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [fx, setFx] = useState({ usd_brl: 5.0, source: 'fallback', ts: null });
  const [brand, setBrand] = useState(OLT_BRANDS[0]);
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('oltm_user');
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  });
  const [permissions, setPermissions] = useState(() => {
    try {
      const raw = localStorage.getItem('oltm_permissions');
      if (raw) return JSON.parse(raw);
    } catch {}
    return null; // null = ainda não carregado; [] = sem nenhuma; ['*'] = tudo
  });
  const [permissionsCatalog, setPermissionsCatalog] = useState(() => {
    try {
      const raw = localStorage.getItem('oltm_perm_catalog');
      if (raw) return JSON.parse(raw);
    } catch {}
    return null; // categorias completas do backend (dinâmico)
  });

  // Recarrega permissões efetivas do backend (chamável a qualquer momento).
  // Em falha, tenta de novo com backoff (2s/5s/10s) antes de assumir fail-closed
  // ([] nega tudo) — nunca deixa `permissions` em null pra sempre (spinner eterno).
  const refreshPermissions = React.useCallback(async (userId, attempt = 0) => {
    const uid = userId || user?.id;
    if (!uid) return null;
    try {
      const { userAPI } = await import('../lib/api');
      const res = await userAPI.get(uid);
      const ep = res.data?.effective_permissions || [];
      setPermissions(ep);
      localStorage.setItem('oltm_permissions', JSON.stringify(ep));
      return ep;
    } catch {
      if (attempt < PERMISSIONS_RETRY_DELAYS_MS.length) {
        await new Promise((resolve) => setTimeout(resolve, PERMISSIONS_RETRY_DELAYS_MS[attempt]));
        return refreshPermissions(uid, attempt + 1);
      }
      setPermissions([]);
      localStorage.setItem('oltm_permissions', JSON.stringify([]));
      toast.error('Não foi possível carregar suas permissões — algumas áreas podem estar indisponíveis.');
      return [];
    }
  }, [user]);

  // Recarrega catálogo completo de permissões (para admin criando novos grupos)
  const refreshPermissionsCatalog = React.useCallback(async () => {
    try {
      const { rbacAPI } = await import('../lib/api');
      const res = await rbacAPI.listPermissions();
      const cats = res.data?.categories || [];
      setPermissionsCatalog(cats);
      localStorage.setItem('oltm_perm_catalog', JSON.stringify(cats));
      return cats;
    } catch { return null; }
  }, []);
  const [whitelabel, setWhitelabelState] = useState(() => {
    try {
      const raw = localStorage.getItem('oltm_whitelabel');
      if (raw) return JSON.parse(raw);
    } catch {}
    return { name: 'OLT MANAGER', logo: null };
  });
  // useCallback → identidade estável (consumido em deps de useEffect/useCallback).
  // Aceita valor OU updater funcional, resolvendo o valor final antes de persistir.
  const setWhitelabel = React.useCallback((wl) => {
    setWhitelabelState((prev) => {
      const next = typeof wl === 'function' ? wl(prev) : wl;
      try { localStorage.setItem('oltm_whitelabel', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  const [throughput, setThroughput] = useState(() => {
    const seed = 380 + Math.random() * 120;
    const arr = [];
    let v = seed;
    for (let i = 0; i < 60; i++) {
      v = Math.max(220, Math.min(720, v + (Math.random() - 0.5) * 70));
      arr.push({
        t: Date.now() - (60 - i) * 1000,
        dl: v,
        ul: v * (0.08 + Math.random() * 0.07),
      });
    }
    return arr;
  });

  useEffect(() => {
    // Tema aleatório + ordem de seções embaralhada são variações visuais só para
    // a landing pública. Dentro do app logado (/app/*) o tema precisa ser fixo,
    // senão os botões "primary" (que usam --theme-primary) mudam de cor a cada
    // reload — os botões de OltDetailPage/OnuPage usam azul fixo (sky-500), então
    // fixamos a mesma cor aqui para os dois sistemas de botão ficarem consistentes.
    if (window.location.pathname.startsWith('/app')) {
      const fixed = { id: 'app-fixed', name: 'APP', primary: '#0ea5e9', primaryGlow: 'rgba(14, 165, 233, 0.45)', accent: '#0ea5e9' };
      setTheme(fixed);
      applyTheme(fixed);
      return;
    }
    const t = pickRandomTheme();
    setTheme(t);
    applyTheme(t);
    setBrand(pickRandomOlt());

    setSectionOrder(prev => {
      const arr = [...prev];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    });
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('oltm_lang');
    if (saved) {
      setLang(saved);
      setCurrency(saved === 'pt' ? 'BRL' : 'USD');
      setGeoResolved(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const nav = (typeof navigator !== 'undefined' ? navigator.language : 'en') || 'en';
      const navPrefix = nav.toLowerCase().split('-')[0];
      const navHint = navPrefix === 'pt' ? 'pt' : navPrefix === 'es' ? 'es' : 'en';
      if (navHint !== 'en') {
        setLang(navHint);
        setCurrency(navHint === 'pt' ? 'BRL' : 'USD');
      }

      try {
        const r = await geoAPI.get();
        if (cancelled) return;
        if (r.data && r.data.locale) {
          const raw = String(r.data.locale).toLowerCase();
          const lg = raw === 'pt' ? 'pt' : raw === 'es' ? 'es' : 'en';
          setLang(lg);
          setCurrency(lg === 'pt' ? 'BRL' : 'USD');
        }
        const cc = (r.data && r.data.country_code) ? r.data.country_code.toUpperCase() : null;
        if (cc) setCountryCode(cc);
      } catch (_) {
        setCurrency(navHint === 'pt' ? 'BRL' : 'USD');
      } finally {
        setGeoResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const r = await statsAPI.live();
        if (!cancelled) setStats(r.data);
      } catch (_) { /* noop */ }
    };
    fetchStats();
    const id = setInterval(fetchStats, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchFx = async () => {
      try {
        const r = await statsAPI.fx();
        if (!cancelled && r.data && r.data.usd_brl) setFx(r.data);
      } catch (_) { /* keep fallback */ }
    };
    fetchFx();
    const id = setInterval(fetchFx, 300000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setThroughput((prev) => {
        const last = prev[prev.length - 1];
        let nextDl = last.dl + (Math.random() - 0.48) * 60;
        if (Math.random() < 0.08) nextDl += (Math.random() - 0.3) * 180;
        nextDl = Math.max(220, Math.min(780, nextDl));
        const nextUl = nextDl * (0.08 + Math.random() * 0.08);
        return [...prev.slice(1), { t: Date.now(), dl: nextDl, ul: nextUl }];
      });
    }, 2000);
    return () => clearInterval(id);
  }, []);

  const toggleLang = () => {
    const cycle = { pt: 'en', en: 'es', es: 'pt' };
    const next = cycle[lang] || 'pt';
    setLang(next);
    localStorage.setItem('oltm_lang', next);
    setCurrency(next === 'pt' ? 'BRL' : 'USD');
  };

  useEffect(() => {
    const saved = localStorage.getItem('oltm_mode');
    setMode(saved === 'dark' ? 'dark' : 'light');
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (mode === 'light') root.classList.add('light');
    else root.classList.remove('light');
  }, [mode]);

  const langFirstRun = React.useRef(true);
  useEffect(() => {
    if (langFirstRun.current) {
      langFirstRun.current = false;
      return;
    }
    document.documentElement.lang = lang === 'pt' ? 'pt-BR' : 'en';
  }, [lang]);

  const toggleMode = () => {
    setMode((m) => {
      const next = m === 'light' ? 'dark' : 'light';
      localStorage.setItem('oltm_mode', next);
      return next;
    });
  };

  const toggleCurrency = () => {
    setCurrency((c) => (c === 'BRL' ? 'USD' : 'BRL'));
  };

  const reshuffle = () => {
    const newTheme = pickRandomTheme();
    setTheme(newTheme);
    applyTheme(newTheme);
    setBrand(pickRandomOlt());
    setSectionOrder(prev => {
      const arr = [...prev];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    });
  };

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('oltm_user', JSON.stringify(userData));
    localStorage.setItem('oltm_token', token);
    // Buscar permissões efetivas + catálogo do backend (dinâmico).
    // refreshPermissions já é fail-closed internamente (retry + backoff, cai
    // para [] em falha definitiva) — não precisa de fallback aqui.
    if (userData?.id) {
      refreshPermissions(userData.id);
      refreshPermissionsCatalog();
    }
  };

  // Usado pela troca de senha obrigatória (must_change_password) — atualiza
  // só o campo relevante do usuário em memória + localStorage, sem refazer
  // login nem recarregar permissões.
  const patchUser = (patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem('oltm_user', JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    setUser(null);
    setPermissions(null);
    setPermissionsCatalog(null);
    localStorage.removeItem('oltm_user');
    localStorage.removeItem('oltm_token');
    localStorage.removeItem('oltm_permissions');
    localStorage.removeItem('oltm_perm_catalog');
  };

  // Re-hidrata permissões + catálogo ao montar (nova aba após login persistido)
  useMount(() => {
    if (user?.id) {
      if (!permissions) refreshPermissions(user.id);
      if (!permissionsCatalog) refreshPermissionsCatalog();
    }
  });

  // Owner sempre tem tudo; '*' wildcard também.
  // Fail-closed: enquanto `permissions` não é um array carregado (null = ainda
  // não veio / falhou antes do fallback definitivo, undefined, ou formato
  // inesperado do backend), NEGA — nunca libera por padrão.
  const hasPermission = (key) => {
    if (!user) return false;
    if (!key) return true;
    if (String(user?.role || '').toLowerCase() === 'owner') return true;
    if (!Array.isArray(permissions)) return false;
    if (permissions.includes('*')) return true;
    return permissions.includes(key);
  };

  // null = ainda não veio (primeira carga ou retry em andamento); [] = veio
  // vazio de propósito (sem permissões) ou fail-closed definitivo.
  const permissionsLoading = !!user && permissions === null;

  const t = useMemo(() => DICT[lang] || DICT.en, [lang]);

  // Os handlers (toggleLang, login, hasPermission, …) são recriados a cada
  // render, mas todo estado que eles capturam (lang, user, permissions, …) já
  // está na lista de deps abaixo — então `value` é recalculado sempre que algo
  // relevante muda e nenhum closure fica velho. Listar as funções só trocaria a
  // identidade de `value` a cada render sem ganho. Memoizá-las com useCallback
  // seria a alternativa "correta" pro lint, mas é refactor separado.
  const value = useMemo(() => ({
    lang, t, theme, sectionOrder, stats, toggleLang, reshuffle, geoResolved,
    countryCode, currency, fx, toggleCurrency, brand, mode, toggleMode,
    whitelabel, setWhitelabel, user, login, logout, patchUser, permissions, hasPermission,
    permissionsLoading, permissionsCatalog, refreshPermissions, refreshPermissionsCatalog,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lang, t, theme, sectionOrder, stats, geoResolved, countryCode, currency, fx, brand, mode, whitelabel, user, permissions, permissionsLoading, permissionsCatalog]);

  return (
    <AppCtx.Provider value={value}>
      <ThroughputCtx.Provider value={throughput}>
        {children}
      </ThroughputCtx.Provider>
    </AppCtx.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}

export function useThroughput() {
  const ctx = useContext(ThroughputCtx);
  if (ctx === null) throw new Error('useThroughput must be used inside AppProvider');
  return ctx;
}
